import axios from "axios";

/**
 * True when a rejection is a background HTTP request the browser aborted on
 * navigation, or one that failed with no response (network unavailable). axios
 * surfaces both as `ERR_CANCELED` / `ERR_NETWORK`. A real server error
 * (`ERR_BAD_REQUEST` / `ERR_BAD_RESPONSE`, i.e. a 4xx/5xx that *did* respond) is
 * deliberately excluded so it still surfaces as a page error.
 */
export function isAbortedOrNetworkRejection(reason: unknown): boolean {
  if (axios.isCancel(reason)) return true;
  if (axios.isAxiosError(reason)) {
    return reason.code === "ERR_NETWORK" || reason.code === "ERR_CANCELED";
  }
  return false;
}

/**
 * Global `unhandledrejection` handler for fire-and-forget background requests.
 *
 * Several list pages refresh reference data without awaiting (`void
 * store.fetch()`) — a deliberately non-blocking pattern. When the user navigates
 * away mid-flight the browser aborts those in-flight requests and axios rejects
 * with "Network Error"; with no local catch that becomes an *unhandled
 * rejection*. It harms nothing the user sees (the page they navigated to loads
 * fresh), but it pollutes the console, is reported as a `pageerror` (which fails
 * the E2E no-error gate non-deterministically — the SLA adherence flow reloads
 * the requests list repeatedly), and would otherwise be captured as an exception.
 *
 * We log it as expected degradation (per the error-handling policy: an optional
 * fetch failed → `console.warn`) and mark it handled. Genuine JS errors and
 * real server-error rejections are left untouched so they still surface.
 */
export function handleBackgroundRejection(event: PromiseRejectionEvent): void {
  if (!isAbortedOrNetworkRejection(event.reason)) return;
  const reason = event.reason as {
    config?: { method?: string; url?: string };
    message?: string;
  };
  const method = reason.config?.method?.toUpperCase() ?? "?";
  const url = reason.config?.url ?? "?";
  console.warn(
    `Background request aborted or network unavailable (ignored): ${method} ${url} — ${
      reason.message ?? "Network Error"
    }`,
  );
  // Mark the rejection handled so it does not surface as an uncaught page error.
  event.preventDefault();
}

/** Register the handler once, on the client only (no-op during SSR). */
export function registerBackgroundRejectionHandler(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("unhandledrejection", handleBackgroundRejection);
}
